import { useMemo } from 'react';
import { Typography, Box, Paper, Grid } from '@mui/material';
import CheckinsList from '@/components/CheckinsList';
import CustomLink from '@/components/general/CustomLink';
import UnreviewedCheckinDetails from '@/components/UnreviewedCheckinDetails';
import { UnreviewedCheckinDetailsType } from '@/components/UnreviewedCheckinDetails/UnreviewedCheckinDetails.component';
import { getEndpointWithParams } from '@/routes/config';
import { TCheckinHistory } from '@/types/api';
import { Paths } from '@/types/routes';
import styles from './CheckinDetails.styles';

type CheckinDetailsPageProps = UnreviewedCheckinDetailsType & {
  checkinsHistory: TCheckinHistory[];
  reviewerName: string;
  isMyCheckinDetails: boolean;
};

const CheckinDetailsPage = function ({
  dueDate,
  answeredAt,
  answers,
  isCheckinReviewed,
  reviewerName,
  isMyCheckinDetails,
  checkinId,
  checkinsHistory,
  handlePostFeedback,
  handleMarkAsReviewed,
  user,
  refetchCheckin,
  feedbacks,
}: CheckinDetailsPageProps): JSX.Element {
  const urlToBack = useMemo(() => {
    if (isMyCheckinDetails) return Paths.MY_CHECKINS;
    return getEndpointWithParams(Paths.USER_CHECKINS, { userId: user?.id });
  }, [isMyCheckinDetails, user?.id]);

  return (
    <Box data-testid="checkin-details-page">
      <CustomLink to={urlToBack} variant="subtitle1" data-testid="go-back-link">
        go back
      </CustomLink>

      <Grid container spacing={2}>
        <Grid item xs={12} md={9}>
          <UnreviewedCheckinDetails
            dueDate={dueDate}
            answeredAt={answeredAt}
            answers={answers}
            isCheckinReviewed={isCheckinReviewed}
            checkinId={checkinId}
            handlePostFeedback={handlePostFeedback}
            handleMarkAsReviewed={handleMarkAsReviewed}
            user={user}
            refetchCheckin={refetchCheckin}
            feedbacks={feedbacks}
          />

          {isCheckinReviewed && reviewerName && (
            <Typography fontSize={14} color="primary">
              Reviewed by: <strong>{reviewerName}</strong>
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={3} sx={{ marginTop: 2 }}>
          <Paper variant="outlined" sx={styles.stickySidebar}>
            <CheckinsList userName={user?.name} checkinsHistory={checkinsHistory} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CheckinDetailsPage;
